import React, { lazy, memo, Suspense, useCallback, useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import _isEmpty from 'lodash/isEmpty';
import { useFeatures } from '@atc/features';
import { sendClick, sendImpressions, DelayedImpressionObserver } from 'reaxl-analytics';

import {
    creditAppGetPreQualifiedClick,
    ndsDealerFinancingCTA,
    ndsInviewImpression,
    ndsEligibleImpression,
    ndsLendingOptionsCTAClick,
    ndsGetPreQualifiedExploreFinancingClick,
    ndsStartCalculatorImpression,
    ndsComponentLoad,
} from 'reaxl-analytics-handlers';

import { DealReceipt } from 'reaxl-wallet';
import { LoadingSpinner } from 'reaxl';

import { eventBus } from '@atc/cai-event-bus';
import { WALLET_GET_DEAL_DETAILS_COMPLETED, WALLET_GET_DEAL_DETAILS_WITH_POLLING_REQUEST } from 'wallet-manager';

import ConfigContext from '../ConfigContext';
import { DealerFinancingCTA, SacaFinancingCTA } from './components';

const NativeDealCalculator = lazy(
    () =>
        import(
            /* webpackPrefetch: true */
            /* webpackChunkName: "NativeDealCalculator" */
            './NativeDealCalculator'
        )
);

const SCA_LOGO_PATH = '/content/dam/autotrader/loans/SecureApp.png';

function ConnectedNativeDealCalculator({
    config = {},
    container, // For Syndicated Calc
    enableCreditAppLink,
    sponsoredInsurance,
    enableSACACreditApp,
    listingId,
    listingVin,
    onCreditAppCTAClick = () => {},
    onEmailCTAClick,
    onFetchPersonalizedPaymentDetails, // For Syndicated Calc
    inventoryDuck,
    paymentsDuck,
    dealsDuck,
    personalization,
    userDuck,
    vehiclePaymentDetails = {}, // For Syndicated Calc
    pageName,
    enableNDSCalc,
    enableExernalFetchForVehicle = false,
    priceLabel = 'MSRP',
    priceDescription = '',
    ...rest
}) {
    const {
        listingPrice,
        supportedDealTypes = ['finance', 'cash'],
        initialDealType = supportedDealTypes[0], // The default has to be one of the supported ones
        isSingleColumn = false,
        isUDSCompact = false,
        enableDealReceipt = true,
    } = config;

    const dispatch = useDispatch();

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const paymentsInfo = useSelector((state) => paymentsDuck?.selectors?.getPaymentsInfo(state)) || {};
    const isPaymentsInfoLoaded =
        useSelector(paymentsDuck.selectors.getIsPaymentsInfoLoaded) || !!onFetchPersonalizedPaymentDetails;

    const existingDeal = useSelector((state) => dealsDuck?.selectors?.getDealByListingVin(state, listingVin)) || {};
    const hasDeal = !_isEmpty(existingDeal);
    const { deal } = existingDeal;
    const hideReceipt =
        useSelector((state) => dealsDuck?.selectors?.getHideReceiptByListingId(state, listingId)) || false;

    const listing = useSelector((state) => state?.inventory?.[listingId]) || {};
    const {
        creditApp,
        finance = [],
        owner: listingOwner, // listing.owner can be undefined, null, owner id (number), or owner object that might have an id
    } = listing;

    const ownerId = typeof listingOwner === 'object' ? listingOwner.id : listingOwner;
    const owner = useSelector((state) => state?.owners?.[ownerId]) || {};
    const { aemHost = '' } = useSelector((state) => state.configs) || {};

    const {
        retail_engine: [enableRetailEngine],
    } = useFeatures(['nds', 'retail_engine']);

    const [showNdsWithDeal, setShowNdsWithDeal] = useState(hasDeal && hideReceipt);
    const [showReceipt, setShowReceipt] = useState(false);

    const [isFetchingDeals, setIsFetchingDeals] = useState(false);
    const enableLoading = () => setIsFetchingDeals(true);
    const disableLoading = () => setIsFetchingDeals(false);

    const hasFiredUDSLoadTagging = useRef(false);

    useEffect(() => {
        eventBus.on(WALLET_GET_DEAL_DETAILS_WITH_POLLING_REQUEST, enableLoading);
        eventBus.on(WALLET_GET_DEAL_DETAILS_COMPLETED, disableLoading);

        return () => {
            eventBus.off(WALLET_GET_DEAL_DETAILS_WITH_POLLING_REQUEST, enableLoading);
            eventBus.off(WALLET_GET_DEAL_DETAILS_COMPLETED, disableLoading);
        };
    }, []);

    useEffect(() => {
        setShowReceipt(hasDeal && !showNdsWithDeal && enableDealReceipt);
    }, [hasDeal, showNdsWithDeal, enableDealReceipt]);

    useEffect(() => {
        setShowNdsWithDeal(hasDeal && hideReceipt);
    }, [hideReceipt, hasDeal]);

    const personalPaymentProps = {
        paymentsEnabled: enableRetailEngine,
    };

    // Handles NDS Calc dealer financing CTA click
    const handleClickDealerFinancingCTA = useCallback(
        (event, href) => {
            event.stopPropagation();
            event.preventDefault();
            sendClick(ndsDealerFinancingCTA, event, {
                listingId,
                cmp: 'vw_dlr_fin_opts',
            });
            window.open(href);
        },
        [listingId]
    );

    // should fire when 'Get Pre-Qualified' link clicked on nds
    const handleCreditAppCTAClick = (event) => {
        onCreditAppCTAClick({ showFinanceAppModal: true });
        sendClick(ndsLendingOptionsCTAClick, event, { listingId, enableCreditApp: enableCreditAppLink });
    };

    // should fire when 'Get Pre-Qualified' link clicked inside 'Explore Financing' modal on nds
    const handleSACACreditAppCTA = (event) => {
        onCreditAppCTAClick({ showFinanceAppModal: true });
        sendClick(ndsGetPreQualifiedExploreFinancingClick, event);
    };

    const onSACAApplicationClick = (event) => {
        onCreditAppCTAClick({ showFinanceAppModal: true });
        sendClick(creditAppGetPreQualifiedClick, event, { listingId, partner: 'saca' });
    };

    const handleThirdPartyCreditAppCTAClick = (event) => {
        sendClick(creditAppGetPreQualifiedClick, event, { listingId, partner: creditApp?.partner, section: 'nds' });
    };

    const addListingToHideReceipt = () => {
        dispatch(dealsDuck.creators.addHideReceiptListing({ listingId }));
    };

    const financingCTAProps = () => {
        const dealerFinancingCTAProps = {
            image: {
                src: aemHost + SCA_LOGO_PATH,
                alt: owner.name,
            },
            owner,
        };

        if (enableSACACreditApp) {
            // Passing SACA
            return {
                ctaElement: <SacaFinancingCTA />,
                handleSACACreditAppCTA,
            };
        }

        if (finance.length > 0 || owner?.dealerFinancingHref) {
            // Passing props or Dealer financing cta tile
            return {
                ctaElement: <DealerFinancingCTA {...dealerFinancingCTAProps} />,
                onClick: (event) =>
                    handleClickDealerFinancingCTA(event, finance?.[0]?.link?.href || owner.dealerFinancingHref),
            };
        }

        return {};
    };

    const isLoading = Object.keys(config).length === 0;

    const handleImpressions = useCallback(() => {
        if (listingId && !pageName) {
            sendImpressions({ name: ndsEligibleImpression, data: { listingId } });
        }
        sendImpressions({ name: ndsInviewImpression, data: { listingId, paymentsInfo, initialDealType } });
        sendImpressions({
            name: ndsStartCalculatorImpression,
            data: {
                listingId,
                walletData: {
                    paymentsInfo,
                    listingPrice,
                },
            },
        });
    }, [listingId, pageName, paymentsInfo, initialDealType, listingPrice]);

    if (!enableNDSCalc) {
        return null;
    }

    if (isLoading || !isPaymentsInfoLoaded) {
        return (
            <LoadingSpinner
                height={563}
                className="display-flex align-items-center align-self-stretch"
            />
        );
    }

    // Assume receipt will show because we're polling, so mimic receipt spinner
    if (isFetchingDeals) {
        return <LoadingSpinner className="margin-vertical-7" />;
    }

    // Must be place under the LoadingSpinner conditions above
    if (hasFiredUDSLoadTagging.current === false) {
        sendClick(ndsComponentLoad, null, {
            paymentsInfo,
        });

        hasFiredUDSLoadTagging.current = true;
    }

    return (
        <>
            {!showReceipt && (
                <DelayedImpressionObserver
                    key={`NDSCalc-${listingId}-delayed-impression-obvserver`}
                    onChange={handleImpressions}
                    delay={0}
                    id="nds"
                >
                    <Suspense fallback={null}>
                        <ConfigContext.Provider value={config}>
                            <NativeDealCalculator
                                container={container}
                                creditApp={
                                    creditApp
                                        ? {
                                              ...creditApp,
                                              onCTAClick: handleThirdPartyCreditAppCTAClick,
                                          }
                                        : undefined
                                }
                                dealerFinancingCTA={financingCTAProps()}
                                dealsDuck={dealsDuck}
                                dealXgData={existingDeal}
                                enableCreditApp={enableCreditAppLink}
                                initialDealType={deal?.dealType?.toLowerCase() || initialDealType}
                                listingId={listingId}
                                listingPrice={listingPrice}
                                listingVin={listingVin}
                                priceLabel={priceLabel}
                                priceDescription={priceDescription}
                                onEmailCTAClick={onEmailCTAClick}
                                onFetchPersonalizedPaymentDetails={onFetchPersonalizedPaymentDetails}
                                onFinancingCTAClick={handleCreditAppCTAClick}
                                paymentsDuck={paymentsDuck}
                                personalization={personalization}
                                personalPaymentProps={personalPaymentProps}
                                showNdsWithDeal={showNdsWithDeal}
                                sponsoredInsurance={sponsoredInsurance}
                                supportedDealTypes={supportedDealTypes}
                                userDuck={userDuck}
                                vehiclePaymentDetails={vehiclePaymentDetails}
                                enableExernalFetchForVehicle={enableExernalFetchForVehicle}
                                {...rest}
                            />
                        </ConfigContext.Provider>
                    </Suspense>
                </DelayedImpressionObserver>
            )}

            {showReceipt && (
                <DealReceipt
                    addListingToHideReceipt={addListingToHideReceipt}
                    dealsDuck={dealsDuck}
                    enableSaca={enableSACACreditApp}
                    inventoryDuck={inventoryDuck}
                    isUDSCompact={isSingleColumn && isUDSCompact}
                    onEmailCTAClick={onEmailCTAClick}
                    onSACAApplicationClick={onSACAApplicationClick}
                    pageName={pageName}
                    paymentsDuck={paymentsDuck}
                    vin={listingVin}
                />
            )}
        </>
    );
}

export default memo(ConnectedNativeDealCalculator);
